"use client";
import { getReportsUrl } from "@areahub/config";
import { loadingCtx } from "@areahub/ui";
import { useContext, useEffect, useState } from "react";
import { useAtom } from "jotai";
import {
  GeoLocationContext,
  RedirectModalContext,
  type PlaceSearchContextType,
} from "../contexts";
import { findClosestTopCity } from "../utils/find-closest-top-city";
import { type RscRouterParameters } from "./rsc-router-params";
import { useSearchResultQueryParams } from "./use-query-params";

export function useCurrentLocationButton(
  modal: PlaceSearchContextType["modal"],
  { router, rsc }: RscRouterParameters,
) {
  // Hooks
  const locationContext = useContext(GeoLocationContext);
  const redirectModalContext = useContext(RedirectModalContext);
  const [isClicked, setClicked] = useState(false);
  const [ctx, setLoadingCtx] = useAtom(loadingCtx);
  const { updateSearchResult, getSearchResult } = useSearchResultQueryParams();

  // Variables
  const [userLocation, getUserLocation] = locationContext;
  const disabled = !userLocation.isSupported || userLocation.isLoading;
  const {
    onClose: onRedirectModalClose,
    onOpen: onRedirectModalOpen,
    saveCallback: saveRedirectModalCallback,
  } = redirectModalContext;

  // Opens the loading screen with new message
  const setLoadingCtxMessage = (message: string) => {
    !rsc &&
      setLoadingCtx({
        ...ctx,
        props: {
          ...ctx.props,
          message: [message],
        },
        isOpen: true,
      });
  };

  // Closes the loading screen
  const unsetLoader = () => {
    !rsc &&
      setLoadingCtx({
        ...ctx,
        isOpen: false,
      });
  };

  const redirectUser = async () => {
    const { error, coords } = userLocation.result;
    if (error) {
      return;
    } else if (coords) {
      !rsc && setLoadingCtxMessage("Generating your report");
      const { lat, lon } = coords;
      const closestTopCity = findClosestTopCity(lat, lon);
      const reportPath = closestTopCity?.path;
      router.push(
        `${getReportsUrl()}/reports/city/${reportPath}?searchedContext=${getSearchResult()}`,
      );
      // Reverse-geocodes lat/lon position and redirects user to report
      // const { url } = await fetch(
      //   `/api/reverse-geocode/?lat=${lat}&lon=${lon}`,
      // ).then((res) => res.json());
      // router.push(`${url}`);
    }
  };

  // Listen for position updates to trigger next steps
  useEffect(() => {
    if (modal) {
      if (isClicked && !userLocation.isLoading) {
        // Close the search modal and the loading screen
        modal.onClose();
        unsetLoader();
        // Save continueToRedirect for later callback
        saveRedirectModalCallback(continueToRedirect, {});
        // Update the search result with the user's location
        updateSearchResult("your current location");
        // Open the redirect modal
        onRedirectModalOpen();
        // Debounce location button
        setClicked(false);
      }
    }
  }, [userLocation.isLoading, modal]);

  // Called to find the user's location
  const findMyLocation = () => {
    setClicked(true);
    !disabled && getUserLocation();
    !rsc && setLoadingCtxMessage("Requesting your location");
  };

  // Redirects user to the report and closes the modal
  const continueToRedirect = () => {
    redirectUser();
    onRedirectModalClose();
  };

  return {
    disabled,
    onClick: findMyLocation,
    isClicked,
  };
}
